import { designVariables } from "@studio3t/design-variables";
import {
  DateTimePickerView,
  Icon,
  IconSVG,
  SVGColorKey,
  useDateTimePicker,
} from "@studio3t/generic-components";
import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styled, { useTheme } from "styled-components";

// A date-and-time field on the design's underline input face (Figma
// AP_SystemLogs/filter 13759-112325): the whole row opens the library's
// calendar panel, a set value clears from the small × riding the field.
// Born in the audit log's timeframe filter, shared with the pipeline
// report download (TTTL-220).

function formatBound(date: Date): string {
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

// A press inside the panel, or inside a floating-ui portal, is not an
// outside press. The panel renders inside the field root, but the time
// unit lists (H / M / S) are portalled to the body by the library, so
// plain containment took the first press on an hour for an outside
// click and unmounted the panel with the list still open.
function isOutsideField(target: Node, root: HTMLElement | null): boolean {
  if (root?.contains(target)) return false;
  return !(
    target instanceof Element &&
    target.closest("[data-floating-ui-portal]") !== null
  );
}

// Outside click / Escape close the panel.
function useDismissOnOutside(
  open: boolean,
  rootRef: RefObject<HTMLElement | null>,
  onDismiss: () => void,
) {
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (isOutsideField(target, rootRef.current)) onDismiss();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, rootRef, onDismiss]);
}

// The × clearing a set bound: an absolute sibling of the field button so
// nothing interactive nests inside it.
function BoundClearButton({
  placeholder,
  testId,
  onClear,
  hovered,
  onHoverChange,
}: {
  placeholder: string;
  testId: string;
  onClear: () => void;
  /** Hover lives in the field, which outlives this button — it only
   *  renders while the bound is set. */
  hovered: boolean;
  onHoverChange: (hovered: boolean) => void;
}) {
  const theme = useTheme();
  return (
    <FieldClearButton
      type="button"
      aria-label={`Clear ${placeholder.toLowerCase()}`}
      title={`Clear ${placeholder.toLowerCase()}`}
      data-testid={`${testId}-clear`}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
      onClick={onClear}
    >
      {/* The glyph is an <img> with the color baked in — CSS can't
          recolor it, so hover feedback rides the icon's own state
          colors (plus the pill on the button). */}
      <IconSVG
        icon={Icon.Close}
        size={12}
        hover={hovered}
        colors={{
          default: theme.semanticTextPlaceholder,
          hover: theme.semanticTextBody,
        }}
      />
    </FieldClearButton>
  );
}

type Picker = ReturnType<typeof useDateTimePicker>;

// The calendar panel itself: the lib picker's exported view wired to the
// hook state that drives it (its stock component would put its own
// trigger input in between).
function TimeframeCalendarPanel({
  label,
  picker,
  onConfirm,
  onCancel,
}: {
  label: string;
  picker: Picker;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <TimeframePopover role="dialog" aria-label={label}>
      <DateTimePickerView
        viewMode={picker.viewMode}
        displayedMonth={picker.displayedMonth}
        displayedYear={picker.displayedYear}
        draftDate={picker.draftDate}
        onToggleViewMode={picker.toggleViewMode}
        onSelectMonth={picker.selectMonth}
        onSelectDay={picker.selectDay}
        onNavigatePrevMonth={picker.navigatePrevMonth}
        onNavigateNextMonth={picker.navigateNextMonth}
        onNavigatePrevYear={picker.navigatePrevYear}
        onNavigateNextYear={picker.navigateNextYear}
        onConfirm={onConfirm}
        onCancel={onCancel}
        timeProps={{
          hours: picker.hours,
          minutes: picker.minutes,
          seconds: picker.seconds,
          milliseconds: picker.milliseconds,
          hideMilliseconds: picker.hideMilliseconds,
          onChangeHours: picker.onChangeHours,
          onChangeMinutes: picker.onChangeMinutes,
          onChangeSeconds: picker.onChangeSeconds,
          onChangeMilliseconds: picker.onChangeMilliseconds,
        }}
      />
    </TimeframePopover>
  );
}

// One bound of the timeframe (Start date / End date): the design's
// underline input face with a decorative calendar glyph — the whole
// field is the button, the icon itself is deliberately NOT interactive.
// Clicking the field opens the calendar panel DIRECTLY. A set bound
// clears via the small × riding the field.
export function DateTimeField({
  label,
  placeholder,
  testId,
  value,
  onChange,
}: {
  /** What the field is called in accessible names (the clear button, the
   *  calendar dialog); the placeholder when omitted. */
  label?: string;
  placeholder: string;
  testId: string;
  value: Date | undefined;
  onChange: (d: Date | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const [clearHovered, setClearHovered] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const picker = useDateTimePicker({
    value,
    onChange: (d) => onChange(d),
  });
  const { open: openPicker, cancel: cancelPicker, confirm } = picker;
  const name = label ?? placeholder;

  const closePanel = useCallback(() => {
    cancelPicker();
    setOpen(false);
  }, [cancelPicker]);

  const togglePanel = () => {
    if (open) {
      closePanel();
    } else {
      openPicker();
      setOpen(true);
    }
  };

  useDismissOnOutside(open, rootRef, closePanel);

  return (
    <TimeframeRoot ref={rootRef}>
      <TimeframeInput
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        // A set value shows only the date, so the name carries the field
        // as well; empty, the visible placeholder is the name.
        aria-label={value ? `${name}: ${formatBound(value)}` : undefined}
        data-testid={testId}
        onClick={togglePanel}
      >
        <TimeframeText $placeholder={value === undefined}>
          {value ? formatBound(value) : placeholder}
        </TimeframeText>
        {/* Decorative — the field itself is the control. */}
        <IconSVG
          icon={Icon.Date}
          size={16}
          svgColorKey={SVGColorKey.semanticTextBody}
        />
      </TimeframeInput>
      {value !== undefined && (
        <BoundClearButton
          placeholder={name}
          testId={testId}
          onClear={() => {
            onChange(undefined);
            if (open) closePanel();
          }}
          hovered={clearHovered}
          onHoverChange={setClearHovered}
        />
      )}
      {open && (
        <TimeframeCalendarPanel
          label={name}
          picker={picker}
          onConfirm={() => {
            confirm();
            setOpen(false);
          }}
          onCancel={closePanel}
        />
      )}
    </TimeframeRoot>
  );
}

const TimeframeRoot = styled.div`
  position: relative;
  width: 100%;
`;

/* The design's underline TextInput face (h 32, 4px side padding, the
   default input border as the bottom rule); the whole row opens the
   picker. A real button — the calendar glyph inside is a plain svg, so
   nothing nests. */
const TimeframeInput = styled.button`
  display: flex;
  align-items: center;
  gap: ${designVariables.ui.input.gapinternal}px;
  width: 100%;
  height: 32px;
  padding: 0 ${designVariables.ui.input.paddingw}px;
  box-sizing: border-box;
  background: transparent;
  border: none;
  border-bottom: 1px solid ${(p) => p.theme.inputDefaultBorder};
  cursor: pointer;
  text-align: left;

  img,
  svg {
    flex-shrink: 0;
  }
`;

const TimeframeText = styled.span<{ $placeholder: boolean }>`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 1.2;
  color: ${(p) =>
    p.$placeholder
      ? p.theme.semanticTextPlaceholder
      : p.theme.semanticTextBody};
`;

/* Positions the calendar panel under its field, above the table — the
   lib picker view brings its own card chrome (shadow, radius, width). */
const TimeframePopover = styled.div`
  position: absolute;
  top: calc(100% + ${designVariables.primitives.spacings.m}px);
  left: 0;
  z-index: 20;
`;

/* The × that clears a set bound: an absolute SIBLING overlaying the
   field's right side (left of the calendar glyph) — inside the field
   button it would nest interactive elements. */
const FieldClearButton = styled.button`
  position: absolute;
  /* A 20px hover pill centered in the field's 32px row. */
  top: 6px;
  right: ${designVariables.ui.input.paddingw + 16 + designVariables.ui.input.gapinternal}px;
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: ${designVariables.ui.button.corner}px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${(p) => p.theme.buttonSubtleHoverBackground};
  }
`;
